import { useEffect, useState } from "react";
import { docTypeApi } from "@services/doctypeApi";
import type { MappingConfigViewModel } from "@models/doctype";

// Module-level cache: the doctype list (and its mapping_config) is small and
// stable for a session, so fetch it once and share across all callers.
let _cache: Promise<Map<string, MappingConfigViewModel | null>> | null = null;

const loadDoctypeMappingMap = (): Promise<
  Map<string, MappingConfigViewModel | null>
> => {
  if (!_cache) {
    _cache = docTypeApi
      .getDoctypes()
      .then((list) => new Map(list.map((d) => [d.docTypeCode, d.mappingConfig])))
      .catch((err) => {
        _cache = null; // allow retry on next mount after a failure
        throw err;
      });
  }
  return _cache;
};

/**
 * Resolve the mapping config for a doctype code. Returns `null` until loaded,
 * when the doctype has no mapping config, or on fetch failure. Used to gate
 * the mapping status/redirect UI: only doctypes whose config is `enabled`
 * connect the mapping WebSocket / poll the mapping job / show the button.
 */
export const useDoctypeMappingConfig = (
  doctypeCode: string | undefined,
): MappingConfigViewModel | null => {
  const [cfg, setCfg] = useState<MappingConfigViewModel | null>(null);

  useEffect(() => {
    if (!doctypeCode) {
      setCfg(null);
      return;
    }
    let active = true;
    loadDoctypeMappingMap()
      .then((map) => {
        if (active) setCfg(map.get(doctypeCode) ?? null);
      })
      .catch(() => {
        if (active) setCfg(null);
      });
    return () => {
      active = false;
    };
  }, [doctypeCode]);

  return cfg;
};
